import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Link,
  Grid,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { motion } from 'framer-motion';

const impactColor = {
  Positive: 'success',
  Negative: 'error',
  Neutral: 'default',
};

const formatNewsDate = (rawDate) => {
  try {
    const cleaned = rawDate?.replace(/^-/, '').trim();
    const dateObj = new Date(cleaned);
    if (isNaN(dateObj.getTime())) return rawDate;

    return dateObj.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  } catch (err) {
    return rawDate;
  }
};

const MotionBox = motion(Box);

const NewsCard = ({ org }) => {
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!org?.name || !org?.address) return;

    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/company-latest-news`,
          {
            params: {
              company_name: org.name,
              address: org.address,
            },
          }
        );
        setNewsData(res.data);
      } catch (err) {
        console.error('Failed to fetch company news:', err);
        setNewsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [org]);

  if (loading) {
    return (
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
           
          backgroundColor: 'rgba(255, 255, 255, 0)',
          borderRadius: 2,
        }}
      >
        <CircularProgress thickness={5} size={48} sx={{ color: "#08AA66" }} />
      </MotionBox>
    );
  }

  if (!newsData) {
    return (
      <Box
        sx={{
           
          backgroundColor: 'rgba(255, 255, 255, 0)',
          p: 2,
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          No news available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 800,
         
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
   
        borderRadius: 2,
      }}
    >
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        📰 {newsData?.name}
      </Typography>

      <Grid container spacing={1}>
        {Array.isArray(newsData?.value) &&
          newsData.value.map((news, index) => (
            <Grid item xs={12} key={index}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0)',
                   
                  transition: '0.3s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap="2rem"
                >
                  <Link
                    href={news?.link}
                    target="_blank"
                    underline="hover"
                    variant="subtitle1"
                    fontWeight="medium"
                    color="primary"
                    lineHeight={'1.6'}
                  >
                    {news.title}
                  </Link>

                  <Chip
                    label={news.impact}
                    color={impactColor[news.impact] || 'default'}
                    size="small"
                    sx={
                      news.impact === 'Negative'
                        ? {
                            animation: 'blink 1s step-start 0s infinite',
                            '@keyframes blink': {
                              '50%': { opacity: 0 },
                            },
                          }
                        : {}
                    }
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  {news.summary}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Date: {formatNewsDate(news.date)}
                </Typography>
              </Box>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default NewsCard;
